import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity } from 'react-native';
import { createInputStyles } from './Input.styles';
import { InputProps } from './InputProps';
import { useTranslation } from 'react-i18next';
import { useScreenTheme } from '../../../../shared/hooks/useScreenTheme';
import OpenEyeIcon from '../../../../shared/assets/icons/openEyeIcon';
import CloseEyeIcon from '../../../../shared/assets/icons/closeEyeIcon';

const Input: React.FC<InputProps> = ({
  label,
  value,
  placeholder,
  secureTextEntry = false,
  keyboardType,
  error,
  onChangeText,
  required = false,
  validators = [],
  isEditable = true,
  customStyles,
  containerStyle,
  innerContainerStyle,
  customLabelStyle,
  testID,
  pointerEvents,
  numberOfLines,
  maxCharCount,
  customIcon,
  hasAccessToEdit = false,
  onSubmitEditing,
  returnKeyType,
  submitBehavior,
  accessibilityLabel,
  accessibilityHint,
  inputRef,
}) => {
  const [internalError, setInternalError] = useState<string | undefined>('');
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<View>(null);
  const { t } = useTranslation();
  const theme = useScreenTheme('inputText');
  const styles = useMemo(() => createInputStyles(theme), [theme]);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  /** Dynamic border color (Figma spec):
   *  focus  → activeBorderColor  (#005FCC)
   *  hover  → hoverBorderColor   (#66ABFF)
   *  default → inputBorder       (#CCE3FF)
   *  Skipped for disabled / non-editable states. */
  const isDisabledStyle = !isEditable && hasAccessToEdit;
  const isNonEditable = !isEditable && !hasAccessToEdit;
  const dynamicBorderColor =
    isDisabledStyle || isNonEditable
      ? undefined
      : isFocused
      ? (theme as any).activeBorderColor ?? theme.inputBorder
      : isHovered
      ? (theme as any).hoverBorderColor ?? theme.inputBorder
      : theme.inputBorder;

  // Click-outside: deselect focus/hover when the user clicks outside the input wrapper
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const node = containerRef.current as any;
      if (node && !node.contains(e.target as Node)) {
        setIsFocused(false);
        setIsHovered(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(prev => !prev);
  };

  const validate = (text: string) => {
    if (required && !text) {
      return t('mandatoryField');
    }
    for (let validator of validators) {
      if (!validator.rule(text)) {
        return validator.message(t);
      }
    }
    return '';
  };

  const handleInputChange = (text: string) => {
    // On web, browsers convert trailing regular spaces to non-breaking spaces
    // (\u00a0) inside <textarea> elements to preserve HTML whitespace rendering.
    // This causes the literal string "&nbsp;" to appear when the saved value is
    // displayed back. Normalize all non-breaking spaces to regular spaces here
    // so that the state (and ultimately the saved note) always contains plain text.
    const normalized = text.replace(/\u00a0/g, ' ');
    onChangeText(normalized);
    setInternalError(validate(normalized));
  };

  // On web, handle multiline height differently
  const webMultilineStyle = numberOfLines && numberOfLines > 1 ? {
    height: 36 * numberOfLines,
    textAlignVertical: 'top' as const,
    paddingTop: 12,
  } : {};

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={[styles.innerContainer, !isEditable && !hasAccessToEdit && innerContainerStyle]}>
        {label && <Text style={[styles.label, customLabelStyle]}>{label}</Text>}
        <View
          ref={containerRef}
          style={styles.inputWrapper}
          // @ts-ignore — onMouseEnter/onMouseLeave are web-only props supported by React Native Web
          onMouseEnter={() => setIsHovered(true)}
          // @ts-ignore
          onMouseLeave={() => setIsHovered(false)}>
          <TextInput
            ref={inputRef}
            value={value}
            placeholder={placeholder}
            secureTextEntry={secureTextEntry && !isPasswordVisible}
            keyboardType={keyboardType}
            style={[
              value === '' ? styles.placeholderInput : styles.input,
              !isEditable && hasAccessToEdit && styles.disabledInput,
              !isEditable && !hasAccessToEdit && styles.nonEditableDisabledInput,
              numberOfLines ? [styles.textArea, customIcon ? styles.iconSpace : undefined] : undefined,
              dynamicBorderColor ? {borderColor: dynamicBorderColor} : undefined,
              customStyles,
              {outline: 'none'} as any,
            ]}
            onChangeText={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            editable={isEditable}
            placeholderTextColor={theme.inputPlaceHolder}
            testID={testID}
            pointerEvents={pointerEvents}
            multiline={!!numberOfLines}
            numberOfLines={numberOfLines}
            maxLength={maxCharCount}
            onSubmitEditing={onSubmitEditing}
            returnKeyType={returnKeyType}
            submitBehavior={submitBehavior}
            accessibilityLabel={accessibilityLabel ?? label ?? placeholder}
            accessibilityHint={
              (error || internalError)
                ? `Error: ${error || internalError}`
                : accessibilityHint
            }
          />

          {/* Web-specific icon rendering */}
          {customIcon && (
            <View
              style={{
                position: 'absolute',
                right: 12,
                top: 0,
                bottom: 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              {customIcon}
            </View>
          )}

          {!customIcon && secureTextEntry && isEditable && (
            <TouchableOpacity
              style={styles.iconContainer}
              onPress={togglePasswordVisibility}
              activeOpacity={0.7}
              testID='secureTextEntryId'
              accessibilityRole="button"
              accessibilityLabel={isPasswordVisible ? t('accessibility.hidePassword') : t('accessibility.showPassword')}
            >
              {isPasswordVisible ? (
                <OpenEyeIcon color={theme.openEyeIcon} />
              ) : (
                <CloseEyeIcon color={theme.closeEyeIcon} />
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      {(error || internalError) && (
        <View accessibilityLiveRegion="polite">
          <Text style={styles.errorText} role="alert">
            {error || internalError}
          </Text>
        </View>
      )}
    </View>
  );
};

export default Input;